import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { StatusCode } from '../../../components/Alert.tsx';
import { Form, FormActions, FormFile, FormInput, FormSwitch, FormTextArea } from '../../../components/form/index.js';
import Image from '../../../components/Image.tsx';
import NutritionalValuesComponent from '../../../components/NutritionalValues.tsx';
import { useAlert } from '../../../contexts/alertProvider.tsx';
import { useRecipeContext } from '../../../contexts/recipes/recipe.tsx';
import { useUsersContext } from '../../../contexts/users.tsx';
import { Role } from '../../../models/enums/role.ts';
import { Recipe } from '../../../models/recipes/recipe.ts';
import { RecipeAliment } from '../../../models/recipes/recipeAliment.ts';
import { RecipeAliments } from './components/RecipeAliments.tsx';
const MAX_FILE_SIZE = 1024 * 1024;//1MB

const validationSchema = Yup.object().shape({
  name: Yup
    .string()
    .required('Name is required')
    .min(2, "Name is too short")
    .max(255, 'Name is too long'),
  servingsNo: Yup
    .number()
    .required('Servings is required')
    .typeError('Servings must be a number')
    .min(0, 'Servings must be a positive number'),
  preparationDuration: Yup
    .number()
    .nullable()
    .typeError('Duration must be a number')
    .min(0, 'Duration must be a positive number'),
  cookingDuration: Yup
    .number()
    .nullable()
    .typeError('Duration must be a number')
    .min(0, 'Duration must be a positive number'),
  description: Yup.string().nullable(),
  isPublished: Yup.boolean(),
  isValidated: Yup.boolean()
});

const RecipeBody = () => {
  const { showAlert } = useAlert();
  const { id } = useParams(); // Get the 'id' from the route parameter
  const userContext = useUsersContext();
  const userIsAdmin = userContext.isInRole(Role.admin) || userContext.isInRole(Role.superAdmin);
  const showIsValidated = userIsAdmin//TODO: || userContext.canValidate;
  const {
    isLoaded,
    recipe,
    insertRecipe,
    updateRecipe } = useRecipeContext();
  const [localRecipe, setLocalRecipe] = useState<Recipe>(recipe);
  useEffect(() => {
    setLocalRecipe(recipe);
  }, [recipe])
  const [canEdit, setCanEdit] = useState(userIsAdmin || recipe.userID === 0 || recipe.userID === userContext.userID);
  useEffect(() => {
    setCanEdit(userIsAdmin || recipe.userID == 0 || recipe.userID == userContext.userID);
  }, [userIsAdmin, recipe, userContext.userID])
  const [file, setFile] = useState<File | null>(null);
  const [fileInputValue, setFileInputValue] = useState<any>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleInputChange = (e: any) => {
    const { id, value } = e.target;

    setLocalRecipe((prevData: Recipe) => ({
      ...prevData,
      [id]: value, // Update the corresponding field in state
    }));

    validationSchema
      .validateAt(id, { ...localRecipe, [id]: value })
      .then(() => setErrors((prev) => ({ ...prev, [id]: '' })))
      .catch((err) => setErrors((prev) => ({ ...prev, [id]: err.message })));
  };

  const handleAlimentInputChange = (e: any) => {
    const { name, value } = e.target;
    const [type, id] = name.split('-');

    var localRecipeAliments = localRecipe.recipeAliments;
    var recipeAliment = localRecipeAliments.find(a => a.alimentID == id);
    if (recipeAliment)
      recipeAliment.amount = value;

    setLocalRecipe((prevData: Recipe) => ({
      ...prevData,
      recipeAliments: localRecipeAliments,
    }));
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files, value } = e.target;
    if (!files || files.length === 0) {
      setFile(null);
      showAlert(StatusCode.warning, "No file selected");
      return;
    }

    const selectedFile = files[0];
    if (selectedFile.size > MAX_FILE_SIZE) {
      setFile(null);
      setFileInputValue(''); // reset input value
      showAlert(
        StatusCode.error,
        `File size too large. Maximum allowed size is ${MAX_FILE_SIZE / (1024 * 1024)} MB`
      );
      return;
    }

    setFile(selectedFile);
    setFileInputValue(value);
  };

  const handleAddItem = (entity: any) => {//TODO: change type any to type Aliment maybe?
    var localRecipeAliments = localRecipe.recipeAliments;

    const existingItem = localRecipeAliments.find(a => a.alimentID == entity.resultID);
    if (existingItem) { showAlert(StatusCode.warning, "Item already exists"); return; }

    localRecipeAliments.push({ recipeAlimentID: 0, alimentID: entity.resultID, aliment: entity, amount: entity.defaultQuantity } as RecipeAliment);

    setLocalRecipe((prevData: Recipe) => ({
      ...prevData,
      recipeAliments: localRecipeAliments,
    }));
  }

  const handleDeleteItem = (entity: RecipeAliment) => {
    const updatedLocalRecipeAliments = [...localRecipe.recipeAliments];
    const entityIndex = updatedLocalRecipeAliments.findIndex(a => a == entity);
    if (entityIndex !== -1) {
      updatedLocalRecipeAliments.splice(entityIndex, 1);
    }

    setLocalRecipe((prevData: Recipe) => ({
      ...prevData,
      recipeAliments: updatedLocalRecipeAliments,
    }));
  }

  const handleSubmit = () => {
    validationSchema
      .validate(localRecipe, { abortEarly: false })
      .then(() => {
        setLocalRecipe(localRecipe);
        if (id == "create")
          insertRecipe(localRecipe, file);
        else
          updateRecipe(localRecipe, file);
      })
      .catch((err) => {
        const validationErrors: Record<string, string> = {};
        err.inner.forEach((error: Yup.ValidationError) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
      });
  };

  return (
    !isLoaded ? <></> :
      <Form>
        {id != undefined && id != "create" &&
          <div className="w-100 d-flex justify-content-center mb-3">
            <Image
              entityType="Recipe"
              entityID={parseInt(id)}
              className="w-auto h-10rem"
            />
          </div>
        }
        <FormInput
          entity={localRecipe}
          handleInputChange={handleInputChange}
          fieldName='name'
          label='Name'
          disabled={!canEdit}
          errors={errors} />
        <FormInput
          entity={localRecipe}
          handleInputChange={handleInputChange}
          fieldName='servingsNo'
          label='Servings'
          type="numeric"
          disabled={!canEdit}
          errors={errors} />
        <FormInput
          entity={localRecipe}
          handleInputChange={handleInputChange}
          fieldName='preparationDuration'
          label='Preparation duration'
          type="numeric"
          disabled={!canEdit}
          errors={errors} />
        <FormInput
          entity={localRecipe}
          handleInputChange={handleInputChange}
          fieldName='cookingDuration'
          label='Cooking duration'
          type="numeric"
          disabled={!canEdit}
          errors={errors} />

        <h6>Aliments</h6>
        <RecipeAliments
          recipeAliments={localRecipe.recipeAliments}
          handleInputChange={handleAlimentInputChange}
          handleAddItem={canEdit ? handleAddItem : undefined}
          handleDeleteItem={handleDeleteItem}
          mb={canEdit ? undefined : 0}
        />

        {recipe.nutritionalValues != null && !canEdit &&
          <NutritionalValuesComponent nutritionalValues={recipe.nutritionalValues} />
        }

        <FormTextArea
          entity={localRecipe}
          handleInputChange={handleInputChange}
          fieldName='description'
          label='Description'
          disabled={!canEdit}
          errors={errors} />
        {canEdit &&
          <FormFile
            fileInputValue={fileInputValue}
            fieldName='Photo'
            handleFileChange={handleFileChange}
          />}
        {canEdit &&
          <FormSwitch
            entity={localRecipe}
            handleInputChange={handleInputChange}
            fieldName='isPublished'
            label='Publish recipe'
          />
        }
        {showIsValidated &&
          <FormSwitch
            entity={localRecipe}
            handleInputChange={handleInputChange}
            fieldName='isValidated'
            label='Validate recipe'
          />
        }
        {canEdit &&
          <FormActions
            mainAction={handleSubmit}
            mainActionText="Save"
            showCancelButton
          />
        }
      </Form>
  );
};

export default RecipeBody;
